// src/public/tests/SocialContextTestExamples.js
import { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Button, Box, Stack, Alert, Divider, LinearProgress
} from '@mui/material';
import { CheckCircle, Cancel, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para imagen draggable
const SortableImage = ({ id, imageUrl, index }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                width: '200px',
                height: '200px',
                border: '3px solid #1976d2',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#fff',
                boxShadow: isDragging ? 6 : 2,
                '&:hover': {
                    boxShadow: 4,
                },
            }}
        >
            <img
                src={imageUrl}
                alt={`Imagen ${id}`}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                }}
            >
                {index + 1}
            </Box>
        </Box>
    );
};

const SocialContextTestExamples = ({ onStartTest, loading }) => {
    const { t } = useTranslation();
    const [imageOrder, setImageOrder] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [feedback, setFeedback] = useState(null);

    const correctOrder = ['a', 'b', 'c', 'd'];

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    // Inicializar con orden aleatorio
    useEffect(() => {
        const shuffled = [...correctOrder].sort(() => Math.random() - 0.5);
        setImageOrder(shuffled);
    }, []);

    // Cargar imágenes dinámicamente
    const imagesCtx = require.context('../assets/social-context', false, /\.(jpg|jpeg|png)$/);
    const imageIndex = imagesCtx.keys().reduce((acc, key) => {
        const base = key.replace('./', '').replace(/\.(jpg|jpeg|png)$/i, '');
        acc[base] = imagesCtx(key);
        return acc;
    }, {});

    const getImageUrl = (letter) => {
        const base = `ex1${letter}`;
        return imageIndex[base] || '';
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (active.id !== over?.id) {
            setImageOrder((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleCheckOrder = () => {
        const isCorrect = JSON.stringify(imageOrder) === JSON.stringify(correctOrder);
        setFeedback({
            isCorrect,
            correctOrder
        });
    };

    return (
        <Container maxWidth="lg">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    {t('reasoningTests.practiceExamples')}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    {t('reasoningTests.practiceExamplesDescription')}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('reasoningTests.example')} 1 {t('reasoningTests.of')} 1
                    </Typography>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                {/* Instrucciones */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {t('reasoningTests.orderImagesChronologically')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('reasoningTests.dragToOrderInstructions')}
                    </Typography>
                </Box>

                {/* Área de drag & drop */}
                <Box sx={{ mb: 4 }}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={imageOrder}
                            strategy={horizontalListSortingStrategy}
                        >
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexWrap: 'nowrap',
                                    gap: 2,
                                    overflowX: 'auto',
                                    width: '100%',
                                }}
                            >
                                {imageOrder.map((letter, index) => (
                                    <SortableImage
                                        key={letter}
                                        id={letter}
                                        imageUrl={getImageUrl(letter)}
                                        index={index}
                                    />
                                ))}
                            </Stack>
                        </SortableContext>

                        <DragOverlay>
                            {activeId ? (
                                <Box
                                    sx={{
                                        width: '200px',
                                        height: '200px',
                                        border: '3px solid #1976d2',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        opacity: 0.8,
                                        boxShadow: 6,
                                    }}
                                >
                                    <img
                                        src={getImageUrl(activeId)}
                                        alt={`Imagen ${activeId}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </Box>

                {/* Indicador de posiciones */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        {['1º', '2º', '3º', '4º'].map((pos) => (
                            <Box
                                key={pos}
                                sx={{
                                    width: '200px',
                                    textAlign: 'center',
                                    py: 1,
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1,
                                    fontWeight: 'bold',
                                    color: 'text.secondary',
                                }}
                            >
                                {pos}
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* Botón verificar orden */}
                {!feedback && (
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Button
                            variant="contained"
                            onClick={handleCheckOrder}
                            size="large"
                            sx={{ minWidth: '200px' }}
                        >
                            {t('reasoningTests.checkOrder')}
                        </Button>
                    </Box>
                )}

                {/* Feedback */}
                {feedback && (
                    <Alert
                        severity={feedback.isCorrect ? 'success' : 'error'}
                        icon={feedback.isCorrect ? <CheckCircle /> : <Cancel />}
                        sx={{ mb: 3 }}
                    >
                        {feedback.isCorrect ? (
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {t('reasoningTests.correct')}
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {t('reasoningTests.incorrect')}
                                </Typography>
                                <Typography variant="body2">
                                    {t('reasoningTests.correctOrderIs')}: {feedback.correctOrder.map((letter, idx) => {
                                        const description = t(`reasoningTests.correctOrderDescriptions.${letter}`);
                                        return `${idx + 1}º = ${description}`;
                                    }).join(', ')}
                                </Typography>
                            </>
                        )}
                    </Alert>
                )}

                {feedback && (
                    <>
                        <Divider sx={{ my: 4 }} />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                {t('reasoningTests.readyToStart')}
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PlayArrow />}
                                onClick={onStartTest}
                                disabled={loading}
                                sx={{ minWidth: '200px' }}
                            >
                                {t('reasoningTests.startTest')}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default SocialContextTestExamples;